import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    Stack,
    Box, FormLabel, TextField, Typography, RadioGroup, FormControlLabel, Radio, Checkbox,
    MenuItem, FormControl,
    Select,
    Paper, Grid
} from "@mui/material";
import DynamicFormOutlinedIcon from "@mui/icons-material/DynamicFormOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { qrDialogPaperSx, QrModalHeader } from "../qrCode/components/qrUi";
import Slide from "@mui/material/Slide";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

export default function ViewFormModal({ show, handleClose, formData }) {

    // Values typed into the preview (kept only while the popup is open; nothing is saved)
    const [values, setValues] = useState({});
    useEffect(() => {
        if (show) setValues({});
    }, [show, formData]);
    const setVal = (idx, v) => setValues((prev) => ({ ...prev, [idx]: v }));

    const labelSx = { display: "block", mb: 0.75, fontSize: 13.5, fontWeight: 600, color: "#334155" };
    const Req = ({ on }) => (on ? <Box component="span" sx={{ color: "#ef4444", mr: 0.25 }}>*</Box> : null);

    const renderPreviewField = (f, idx) => {
        const multi = ["checkbox", "Dropdown-Multi"].includes(f.field_type);
        const value = values[idx] ?? (multi ? [] : "");

        return (
            <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
                {/* TEXT / TEXTAREA / NUMBER / DATE */}
                {["text", "textarea", "number", "date"].includes(f.field_type.toLowerCase()) && (
                    <>
                        <FormLabel sx={labelSx}>
                            <Req on={f.is_required} />{f.field_name}
                        </FormLabel>
                        <TextField
                            fullWidth
                            size="small"
                            value={value}
                            onChange={(e) => setVal(idx, e.target.value)}
                            type={f.field_type === "date" ? "date" : f.field_type === "number" ? "number" : "text"}
                            multiline={f.field_type === "textarea"}
                            minRows={f.field_type === "textarea" ? 2 : undefined}
                            placeholder={f.field_type === "date" ? undefined : `Enter ${String(f.field_name || "").toLowerCase()}`}
                            inputProps={f.field_length ? { maxLength: Number(f.field_length) || undefined } : undefined}
                        />
                    </>
                )}

                {/* RADIO */}
                {f.field_type === "radio" && (
                    <Box>
                        <FormLabel sx={labelSx}>
                            <Req on={f.is_required} />{f.field_name}
                        </FormLabel>
                        <RadioGroup value={value} onChange={(e) => setVal(idx, e.target.value)} row>
                            {f.options.map((opt, i) => (
                                <FormControlLabel
                                    key={i}
                                    value={opt.value}
                                    control={<Radio size="small" />}
                                    label={opt.label}
                                />
                            ))}
                        </RadioGroup>
                    </Box>
                )}

                {/* CHECKBOX */}
                {f.field_type === "checkbox" && (
                    <Box>
                        <FormLabel sx={labelSx}>
                            <Req on={f.is_required} />{f.field_name}
                        </FormLabel>
                        {f.options.map((opt, i) => (
                            <FormControlLabel
                                key={i}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={value.includes(opt.value)}
                                        onChange={(e) => setVal(idx, e.target.checked ? [...value, opt.value] : value.filter((v) => v !== opt.value))}
                                    />
                                }
                                label={opt.label}
                            />
                        ))}
                    </Box>
                )}

                {/* DROPDOWN (SINGLE / MULTI) */}
                {(f.field_type === "Dropdown-Single" || f.field_type === "Dropdown-Multi") && (
                    <Box>
                        <FormLabel sx={labelSx}>
                            <Req on={f.is_required} />{f.field_name}
                        </FormLabel>
                        <FormControl fullWidth size="small">
                            <Select
                                multiple={f.field_type === "Dropdown-Multi"}
                                value={value}
                                displayEmpty
                                onChange={(e) => setVal(idx, e.target.value)}
                                renderValue={(selected) =>
                                    (Array.isArray(selected) ? selected.length === 0 : !selected)
                                        ? <Box component="span" sx={{ color: "#9ca3af" }}>Select {f.field_name}</Box>
                                        : (Array.isArray(selected) ? selected.join(", ") : selected)
                                }
                            >
                                {f.options.map((opt, i) => (
                                    <MenuItem key={i} value={opt.value}>
                                        {f.field_type === "Dropdown-Multi" && (
                                            <Checkbox size="small" checked={value.includes(opt.value)} />
                                        )}
                                        <Typography
                                            variant="body2"
                                            sx={{ ml: f.field_type === "Dropdown-Multi" ? 1 : 0 }}
                                        >
                                            {opt.label}
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}
            </Grid>
        );
    };


    return (
        <>
            <Dialog
                open={show}
                onClose={handleClose}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
                keepMounted
                PaperProps={{ sx: qrDialogPaperSx }}
            >
                {/* Header */}
                <QrModalHeader
                    icon={<DynamicFormOutlinedIcon />}
                    title={`View ${formData[0]?.organization_name}` ?? "view Organization"}
                    subtitle={formData[0]?.form_name || undefined}
                    onClose={handleClose}
                />

                {/* Content */}
                <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 }, bgcolor: "#f8fafc" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                            <VisibilityOutlinedIcon sx={{ fontSize: 17, color: "#64748b" }} />
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "#64748b" }}>
                                Form preview
                            </Typography>
                            <Box component="span" sx={{ px: 0.9, py: 0.1, borderRadius: 5, fontSize: 11.5, fontWeight: 800, color: "#2563eb", bgcolor: "#2563eb12" }}>
                                {(formData[0]?.fields || []).length} fields
                            </Box>
                        </Stack>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 1.5, sm: 2.5 },
                                borderRadius: 3,
                                bgcolor: "#fff",
                                border: "1px solid #e9edf2",
                                boxShadow: "0 4px 14px -8px rgba(15,23,42,0.15)",
                                // preview inputs: rounded, light border, blue focus
                                // editable inputs: white, light border, blue focus
                                "& .MuiOutlinedInput-root": { borderRadius: 2.5, bgcolor: "#fff", fontSize: 14 },
                                "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                                "& .MuiFormLabel-root.Mui-focused": { color: "#334155" },
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb", borderWidth: "1.5px" },
                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2563eb" },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#2563eb" },
                                "& .MuiRadio-root.Mui-checked, & .MuiCheckbox-root.Mui-checked": { color: "#2563eb" },
                            }}
                        >
                            <Grid container spacing={2}>
                                {formData[0]?.fields.map((f, i) => renderPreviewField(f, i))}
                            </Grid>
                        </Paper>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}
